import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { selectConfig, selectReferral } from '../../redux/selectors';
import { Button, CheckCircle, Input, Colors, LoadingClaro } from 'common-ui-components';
import { EMAIL_REGEX, PHONE_REGEX } from '../../utils/constants';
import { createPhoneChangeHandler } from '../../utils/utils';
import { endReferralProcess, setProcessId, setReferralPersonalData } from '../../redux/Slice/referralSlice';
import { NumericInput } from '../../components/NumericInput';
import { navigateTo } from '../../redux/Slice/navigationSlice';
import { AlphabeticInput } from '../../components/AlphabeticInput';

const PersonalData = () => {
  const { personalData, referralIdu, mapData, loading } = useSelector(selectReferral);
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      documentNumber: personalData.documentNumber || '',
    },
  });
  const dispatch = useDispatch();
  const {
    language: {
      referral: { personalData: text },
    },
  } = useSelector(selectConfig);

  const onSubmit = async (data) => {
    dispatch(setReferralPersonalData(data));
    try {
      const request = {
        ideType: personalData.documentType,
        ideNumber: personalData.documentNumber,
        name: data.name,
        surname: data.lastname,
        contactPhone: data.cellularNumber,
        email: data.email,
        idu: referralIdu,
        addressCoverage: {
          salesEnabled: mapData?.HabilitadoVenta ? 'Y' : 'N',
          coverage: mapData?.Cobertura,
          technology: mapData?.Tecnologia,
          address: {
            street: mapData?.Calle,
            number: mapData?.Numero,
            city: mapData?.Comuna,
            district: mapData?.Region,
            province: mapData?.Provincia,
            cpa: mapData?.CP_A,
            cp: mapData?.CodigoPostal,
          },
        },
      };
      const response = await dispatch(endReferralProcess(request)).unwrap();
      if (response?.status !== '200') {
        throw new Error('Error');
      }
      dispatch(setProcessId(response?.processId));
      dispatch(navigateTo('EndReferralProcess'));
    } catch (error) {
      if (error?.errorCode === '100016') {
        dispatch(navigateTo('InvitationExpired'));
        return;
      }
      dispatch(navigateTo('UnexpectedError'));
    }
  };

  return (
    <section className='section personalData'>
      {loading && (
        <LoadingClaro
          text={'Validando datos... Esperá por favor.'}
          fullViewportSize={true}
          overlay={true}
        />
      )}
      <div className='personalData-headerContainer'>
        <div className='personalData-banner'>
          <CheckCircle color={Colors.verdeInstitucional} size={20} />
          <span>{text.banner}</span>
        </div>
        <h2 className='personalData-title'>{text.title}</h2>
        <span className='personalData-subtitle'>{text.subtitle}</span>
      </div>
      <form className='personalData-form' onSubmit={handleSubmit(onSubmit)}>
        <div className='personalData-inputWrapper'>
          <Controller
            name='name'
            defaultValue=''
            control={control}
            rules={{ required: { value: true, message: 'Obligatorio' } }}
            render={({ field: { name, onChange, ref, value, disabled } }) => (
              <AlphabeticInput
                id='name'
                name={name}
                onChange={onChange}
                ref={ref}
                value={value}
                disabled={disabled}
                typeInput='text'
                data-testid='name-input'
                labelText={
                  <>
                    Nombre <span className='mandatory'>*</span>
                  </>
                }
                iconPosition='trailing-icon'
                status={value ? 'success' : 'default'}
                maxLength={60}
              >
                {value ? <CheckCircle size={21} /> : <></>}
              </AlphabeticInput>
            )}
          />
        </div>
        <div className='personalData-inputWrapper'>
          <Controller
            name='lastname'
            defaultValue=''
            control={control}
            rules={{ required: { value: true, message: 'Obligatorio' } }}
            render={({ field: { name, onChange, ref, value, disabled } }) => (
              <AlphabeticInput
                id='lastname'
                name={name}
                onChange={onChange}
                ref={ref}
                value={value}
                disabled={disabled}
                typeInput='text'
                data-testid='lastname-input'
                labelText={
                  <>
                    Apellido <span className='mandatory'>*</span>
                  </>
                }
                iconPosition='trailing-icon'
                status={value ? 'success' : 'default'}
                maxLength={60}
              >
                {value ? <CheckCircle size={21} /> : <></>}
              </AlphabeticInput>
            )}
          />
        </div>
        <div className='personalData-inputWrapper'>
          <Controller
            name='documentNumber'
            defaultValue=''
            control={control}
            rules={{ required: { value: true, message: 'Obligatorio' } }}
            render={({ field: { name, onChange, ref, value } }) => (
              <NumericInput
                id='documentNumber'
                name={name}
                onChange={onChange}
                ref={ref}
                value={value}
                data-testid='documentNumber-input'
                labelText={
                  <>
                    Número de documento <span className='mandatory'>*</span>
                  </>
                }
                iconPosition='trailing-icon'
                status={value ? 'success' : 'default'}
                readOnly
              >
                {value ? <CheckCircle size={21} /> : <></>}
              </NumericInput>
            )}
          />
        </div>
        <div className='personalData-inputWrapper'>
          <Controller
            name='cellularNumber'
            defaultValue=''
            control={control}
            rules={{
              required: { value: true, message: 'Código de área + sin el 0 y sin el 15. Ej: 3382404505' },
              pattern: {
                value: PHONE_REGEX,
                message: 'Código de área + sin el 0 y sin el 15. Ej: 3382404505',
              },
            }}
            render={({ field: { name, onChange, ref, value }, fieldState: { error } }) => (
              <NumericInput
                id='cellularNumber'
                name={name}
                onChange={createPhoneChangeHandler(onChange, value)}
                ref={ref}
                value={value}
                data-testid='cellularNumber-input'
                helpText={error ? error.message : 'Código de área + sin el 0 y sin el 15. Ej: 3382404505'}
                labelText={
                  <>
                    Teléfono de contacto <span className='mandatory'>*</span>
                  </>
                }
                iconPosition='trailing-icon'
                status={error ? 'error' : value ? 'success' : 'default'}
                maxLength={13}
              >
                {value && !error ? <CheckCircle size={21} /> : <></>}
              </NumericInput>
            )}
          />
        </div>
        <div className='personalData-inputWrapper'>
          <Controller
            name='email'
            defaultValue=''
            control={control}
            rules={{
              required: { value: true, message: 'Obligatorio' },
              pattern: { value: EMAIL_REGEX, message: 'Formato incorrecto' },
              maxLength: 60,
            }}
            render={({
              field: { name, onChange, ref, value, disabled },
              fieldState: { invalid, isDirty },
            }) => (
              <Input
                id='email'
                name={name}
                onChange={onChange}
                ref={ref}
                value={value}
                disabled={disabled}
                typeInput='email'
                data-testid='email-input'
                labelText={
                  <>
                    Email <span className='mandatory'>*</span>
                  </>
                }
                iconPosition='trailing-icon'
                status={isDirty && !invalid ? 'success' : 'default'}
                maxLength={60}
              >
                {isDirty && !invalid ? <CheckCircle size={21} /> : <></>}
              </Input>
            )}
          />
        </div>
        <div className='personalData-buttonsContainer'>
          <span className='personalData-requiredFields'>
            Los campos marcados con (<span className='mandatory'>*</span>) son obligatorios
          </span>
          <Button
            className='submit-button-error'
            type='submit'
            disabled={!isValid}
            data-testid='personalData-continue-button'
          >
            {text.button}
          </Button>
          <a
            onClick={() => dispatch(navigateTo('ValidateCoverage'))}
            data-testid='personaldata-restart-button'
            className='link-button personalData-restart-button'
          >
            {text.secondaryButton}
          </a>
        </div>
      </form>
    </section>
  );
};

export default PersonalData;
